
import { defineComponent, h, type PropType } from "vue"
import MarketingCampaign, {
    MarketingPlanningSaveError,
    type MarketingIntegrationStatus,
    type MarketingMeasurementScope,
    type MarketingMeasurementScopeSuggestion,
} from "packs/models/MarketingCampaign"

const ScopeField = defineComponent({
    name: "ScopeField",
    props: {
        modelValue: { type: String, default: "" },
        label: { type: String, required: true },
        multiline: Boolean,
        disabled: Boolean,
        placeholder: { type: String, default: undefined },
    },
    emits: ["update:modelValue"],
    setup(props, { emit }) {
        return () => h("label", [
            h("span", props.label),
            h(props.multiline ? "textarea" : "input", {
                value: props.modelValue,
                rows: props.multiline ? 3 : undefined,
                disabled: props.disabled,
                placeholder: props.placeholder,
                onInput: (event: Event) => emit("update:modelValue", (event.target as HTMLInputElement).value),
            }),
        ])
    },
})

const listText = (value: any) => Array.isArray(value) ? value.join("\n") : `${value || ""}`
const lines = (value: string) => `${value || ""}`.split(/\r?\n/).map(item => item.trim()).filter(Boolean)
const SCOPE_FIELD_LABELS: Record<string, string> = {
    data_stream_id: "data stream ID", hostnames: "対象host", paths: "対象path", public_page_urls: "公開ページURL",
    service_lp_urls: "サービスLP URL", time_zone: "timezone", key_event_status: "key event確認状態", article_only: "記事専用",
    canonical_host: "canonical host", url_prefixes: "対象URL範囲", article_url_prefixes: "記事URL範囲",
}
const LIST_FIELDS = ["hostnames", "paths", "url_prefixes", "article_url_prefixes", "public_page_urls", "service_lp_urls", "key_event_names"]
const scopeIsEmpty = (scope: any) => !scope || Object.values(scope).every(value => Array.isArray(value) ? value.length == 0 : !value)

export default defineComponent({
    name: "MarketingCampaignSetupWizard",
    components: { ScopeField },
    props: {
        campaign: { type: Object as PropType<MarketingCampaign>, required: true },
        integrations: { type: Array as PropType<MarketingIntegrationStatus[]>, default: () => [] },
        campaignWritable: { type: Boolean, default: true },
        integrationWritable: { type: Boolean, default: true },
    },
    emits: ["campaign-saved", "scope-saved"],
    data() {
        return {
            goalConfigured: false,
            targetUrlsText: "",
            finalConversionEvent: "",
            campaignSaving: false,
            scopeSavingId: "",
            importingIntegrationId: "",
            importFiles: {} as Record<string, File>,
            importPeriods: {} as Record<string, { start: string; end: string }>,
            scopeForms: {} as Record<string, Record<string, string>>,
            articleOnlyForms: {} as Record<string, boolean>,
            suggestions: {} as Record<string, MarketingMeasurementScopeSuggestion>,
            suggestionApplied: {} as Record<string, boolean>,
            suggestionsRequestedFor: "",
            message: "",
            messageKind: "",
            eventOptions: [
                { value: "article_view", label: "記事閲覧" },
                { value: "article_cta_impression", label: "記事CTA表示" },
                { value: "article_cta_click", label: "記事CTAクリック" },
                { value: "service_lp_arrival", label: "サービスLP到着" },
                { value: "form_view", label: "フォーム表示" },
                { value: "form_start", label: "フォーム開始" },
                { value: "form_complete", label: "フォーム完了" },
                { value: "booking_confirmed", label: "予約確定" },
            ],
        }
    },
    computed: {
        targetUrls(): string[] { return lines(this.targetUrlsText) },
        hasKeywords(): boolean { return (MarketingCampaign.planningOf(this.campaign).target_keywords || []).length > 0 },
        setupReady(): boolean { return this.hasKeywords && this.goalConfigured && this.targetUrls.length > 0 && !!this.finalConversionEvent },
        canSaveCampaign(): boolean { return this.campaignWritable && !this.campaignSaving && this.targetUrls.length > 0 && !!this.finalConversionEvent },
        scopedIntegrations(): MarketingIntegrationStatus[] {
            return this.integrations.filter(item => !!item.id && ["ga4", "gsc", "wordpress"].includes(item.provider))
        },
    },
    watch: {
        "campaign.id": { immediate: true, handler() { this.applyCampaign(); this.loadGoal() } },
        integrations: { deep: true, immediate: true, handler() { this.applyIntegrations() } },
    },
    methods: {
        applyCampaign() {
            const planning = MarketingCampaign.planningOf(this.campaign)
            this.targetUrlsText = listText(planning.target_urls)
            this.finalConversionEvent = planning.final_conversion_event || ""
        },
        async loadGoal() {
            const goal = await MarketingCampaign.fetchGoal(this.campaign.id)
            this.goalConfigured = goal?.configured == true
        },
        applyIntegrations() {
            this.scopedIntegrations.forEach(integration => {
                const scope: any = integration.measurement_scope || {}
                this.scopeForms[integration.id] = {
                    data_stream_id: scope.data_stream_id || "",
                    hostnames: listText(scope.hostnames), paths: listText(scope.paths),
                    canonical_host: scope.canonical_host || "", url_prefixes: listText(scope.url_prefixes),
                    article_url_prefixes: listText(scope.article_url_prefixes),
                    public_page_urls: listText(scope.public_page_urls), service_lp_urls: listText(scope.service_lp_urls),
                    time_zone: scope.time_zone || integration.time_zone || (integration.provider == "gsc" ? "America/Los_Angeles" : "Asia/Tokyo"),
                    key_event_names: listText(scope.key_event_names), key_event_status: scope.key_event_status || "unknown",
                    generative_ai_report_access: scope.generative_ai_report_access || "unknown",
                    article_tag_status: scope.article_tag_status || "unknown",
                }
                this.articleOnlyForms[integration.id] = scope.article_only == true
                this.suggestionApplied[integration.id] = false
                if (!this.importPeriods[integration.id]) this.importPeriods[integration.id] = { start: "", end: "" }
                if (!integration.scope_confirmed_at && scopeIsEmpty(integration.measurement_scope)) this.applySuggestion(integration)
            })
            this.loadSuggestions()
        },
        // 未保存の接続だけ提案値を取得する。自動保存はしない。
        async loadSuggestions() {
            const campaignId = this.campaign.id
            if (this.suggestionsRequestedFor == campaignId) return
            if (!this.scopedIntegrations.some(integration => !integration.scope_confirmed_at)) return
            this.suggestionsRequestedFor = campaignId
            this.suggestions = (await MarketingCampaign.fetchIntegrationScopeSuggestions(campaignId)) || {}
            this.scopedIntegrations.forEach(integration => {
                if (!integration.scope_confirmed_at && scopeIsEmpty(integration.measurement_scope)) this.applySuggestion(integration)
            })
        },
        applySuggestion(integration: MarketingIntegrationStatus) {
            const suggestion = this.suggestions[integration.id]
            const form = this.scopeForms[integration.id]
            if (!suggestion || !form) return
            const scope: any = suggestion.scope || {}
            Object.keys(scope).forEach(field => {
                if (field == "article_only") return
                form[field] = LIST_FIELDS.includes(field) ? listText(scope[field]) : `${scope[field] ?? ""}`
            })
            // GA4のtimezoneは推測できないため、提案では空にしてGA4管理画面での確認を促す。
            if (integration.provider == "ga4" && !scope.time_zone) form.time_zone = ""
            if (integration.provider == "wordpress" && !scope.time_zone) form.time_zone = ""
            this.articleOnlyForms[integration.id] = scope.article_only == true
            this.suggestionApplied[integration.id] = true
        },
        fieldLabel(field: string) { return SCOPE_FIELD_LABELS[field] || field },
        async saveCampaignSetup() {
            if (!this.canSaveCampaign) return
            this.campaignSaving = true
            try {
                // 本番同期施策でもローカル設定として保存する（同期で上書きされない）。
                const campaign = await MarketingCampaign.updatePlanning(
                    this.campaign.id,
                    { target_urls: this.targetUrls, final_conversion_event: this.finalConversionEvent as any },
                    this.campaign.local_planning_version || 0
                )
                this.$emit("campaign-saved", campaign)
                this.messageKind = "success"; this.message = "対象URLと最終成果を保存しました。"
            } catch (error: any) {
                this.messageKind = "error"
                this.message = error instanceof MarketingPlanningSaveError && error.message
                    ? `対象URLと最終成果を保存できませんでした。${error.message}`
                    : "対象URLと最終成果を保存できませんでした。"
            } finally {
                this.campaignSaving = false
            }
        },
        scopePayload(integration: MarketingIntegrationStatus): MarketingMeasurementScope {
            const form = this.scopeForms[integration.id] || {}
            if (integration.provider == "ga4") {
                const articleOnly = !!this.articleOnlyForms[integration.id]
                return {
                    data_stream_id: form.data_stream_id, hostnames: lines(form.hostnames), paths: lines(form.paths),
                    // 記事専用は2項目を明示的に空で保存する。
                    public_page_urls: articleOnly ? [] : lines(form.public_page_urls),
                    service_lp_urls: articleOnly ? [] : lines(form.service_lp_urls),
                    time_zone: form.time_zone, key_event_names: lines(form.key_event_names), key_event_status: form.key_event_status as any,
                    article_only: articleOnly,
                }
            }
            if (integration.provider == "gsc") return {
                canonical_host: form.canonical_host, url_prefixes: lines(form.url_prefixes), time_zone: form.time_zone,
                generative_ai_report_access: form.generative_ai_report_access as any,
            }
            return {
                article_url_prefixes: lines(form.article_url_prefixes), time_zone: form.time_zone,
                article_tag_status: (form.article_tag_status || "unknown") as any,
            }
        },
        async saveScope(integration: MarketingIntegrationStatus) {
            this.scopeSavingId = integration.id
            const saved = await MarketingCampaign.updateIntegrationScope(this.campaign.id, integration.id, this.scopePayload(integration))
            this.scopeSavingId = ""
            if (!saved) { this.messageKind = "error"; this.message = "接続scopeを保存できませんでした。"; return }
            this.suggestionApplied[integration.id] = false
            this.$emit("scope-saved", saved)
            this.messageKind = "success"; this.message = `${integration.provider_label}の計測scopeを保存しました。`
        },
        selectImportFile(integrationId: string, event: Event) {
            const file = (event.target as HTMLInputElement).files?.[0]
            if (file) this.importFiles[integrationId] = file
        },
        async importGenerativeAi(integration: MarketingIntegrationStatus) {
            const file = this.importFiles[integration.id]
            if (!file) return
            const period = this.importPeriods[integration.id] || { start: "", end: "" }
            this.importingIntegrationId = integration.id
            const result = await MarketingCampaign.importGenerativeAiPerformance(this.campaign.id, integration.id, file, period.start, period.end)
            this.importingIntegrationId = ""
            if (!result) { this.messageKind = "error"; this.message = "生成AI検索の正式exportを取り込めませんでした。"; return }
            this.messageKind = "success"; this.message = `${result.imported_rows}行の正式exportを取り込みました。`
            this.$emit("scope-saved", integration)
        },
    },
})
